import type { ChangeEvent } from 'react';
import { useTranslation } from 'react-i18next';
import { Flex, Switch, Tab, TabList, Tabs, Text } from '@chakra-ui/react';

import { ApiDocumentSource } from '@izimi/api';
import { Spinner, useDocumentFiltering } from '@izimi/components';
import { useDocumentSelection, useQueryParams } from '@izimi/hooks';
import { useModules } from '@izimi/modules';

const notaryFilterArray = ['all', 'citizen', 'notary', 'fodfin', 'vlabel'] as const;
type NotaryFilter = (typeof notaryFilterArray)[number];

export const isNotaryFilter = (filter: unknown): filter is NotaryFilter =>
  notaryFilterArray.includes(filter as NotaryFilter);

const NotaryFilters = () => {
  const { t } = useTranslation('vault');
  const { params, updateQueryParams } = useQueryParams();
  const {
    documents: { usePagedDocuments },
  } = useModules();
  const { uniqueFilterValues, isLoading } = usePagedDocuments({
    type: 'notaryFilters',
    params: {
      sources: [ApiDocumentSource.Tuyeau, ApiDocumentSource.Vlabel],
      returnUniqueFilterValues: true,
    },
  });
  const { onlyUnseen, setFilters, filters } = useDocumentFiltering();
  const { clearDocumentSelection } = useDocumentSelection();

  const handleUnseenChange = (event: ChangeEvent<HTMLInputElement>) => {
    setFilters({ ...filters, unSeen: event.target.checked });
    clearDocumentSelection();
  };

  const hasFodFin = uniqueFilterValues?.sources?.includes(ApiDocumentSource.Tuyeau);

  const hasVlabel = uniqueFilterValues?.sources?.includes(ApiDocumentSource.Vlabel);

  const availableFilters = notaryFilterArray.filter((filter) => {
    if (filter === 'fodfin' && !hasFodFin) {
      return false;
    }
    if (filter === 'vlabel' && !hasVlabel) {
      return false;
    }
    return true;
  });

  const current = isNotaryFilter(params.filter) ? params.filter : 'all';
  const currentIndex = availableFilters.indexOf(current);

  const getFilterLabel = ({ filter }: { filter: NotaryFilter }) => {
    const filtersLabels: Record<NotaryFilter, string> = {
      all: t('notaryFilters.all'),
      citizen: t('notaryFilters.citizen'),
      notary: t('notaryFilters.notary'),
      fodfin: t('notaryFilters.fodfin'),
      vlabel: t('notaryFilters.vlabel'),
    };

    return filtersLabels[filter];
  };

  return (
    <Flex direction='row' align='center' w='100%' mb={4} px={4}>
      <Tabs defaultIndex={currentIndex >= 0 ? currentIndex : 0}>
        <TabList>
          {availableFilters.map((value) => (
            <Tab
              flex='initial'
              key={value}
              onClick={() => updateQueryParams([{ key: 'filter', value }])}>
              {getFilterLabel({ filter: value })}
            </Tab>
          ))}
          {isLoading && (
            <Tab flex='initial' disabled>
              <Spinner size={6} />
            </Tab>
          )}
        </TabList>
      </Tabs>
      <Flex justifyContent='space-between' alignItems='center' marginLeft='auto' columnGap={1.5}>
        <Text variant='label' color='primary500' pb={0.5}>
          {t('notaryFilters.unSeen')}
        </Text>
        <Switch size='sm' isChecked={onlyUnseen()} onChange={handleUnseenChange} />
      </Flex>
    </Flex>
  );
};

export default NotaryFilters;
